import { useSyncExternalStore } from 'react';
import type { PlaylistPrivacy } from '@visious/core';

/**
 * The privacy this session **set**, per playlist id — the only exact source of a playlist's privacy
 * there is.
 *
 * No response carries `privacy`. Not the list, not the authenticated read, not the `201` from creating
 * one; `isListed` is a boolean over three settings, so `false` means "unlisted *or* private" and nothing
 * in the payload narrows it (`playlist/manage.ts` has the matrix). What the app does know is what it
 * itself sent one round trip ago — and a browser pass showed why holding that per screen is not enough:
 * creating a playlist as **unlisted** and landing on its page rendered "Not public", the app forgetting
 * something it had just been told, because the create happened in one hook's state and the page read
 * another's.
 *
 * So it is a module store, at the same layer as `platform/channelMetaStore.ts` and for the same reason —
 * two screens, one fact — with two deliberate differences:
 *
 * - **In memory, not `localStorage`.** It is a cache of *our own writes*, and a stale entry here is a
 *   confident lie rather than a slow render: a playlist made private from Invidious's own pages in
 *   another tab would keep reading "Unlisted" across reloads for as long as the entry survived. Losing
 *   it on reload costs the vague label the API forces anyway, which is the honest fallback.
 * - **No eviction policy.** An entry is ~40 bytes and the bound is how many playlists one session
 *   edits, so R14's LRU would be machinery around a number that does not grow.
 */

let known: ReadonlyMap<string, PlaylistPrivacy> = new Map();
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): ReadonlyMap<string, PlaylistPrivacy> {
  return known;
}

/** Called after a create or a `PATCH` that carried `privacy` — never after a read. */
export function rememberPlaylistPrivacy(playlistId: string, privacy: PlaylistPrivacy): void {
  known = new Map(known).set(playlistId, privacy);
  for (const listener of listeners) listener();
}

export function forgetPlaylistPrivacy(playlistId: string): void {
  if (!known.has(playlistId)) return;
  const next = new Map(known);
  next.delete(playlistId);
  known = next;
  for (const listener of listeners) listener();
}

export function useKnownPlaylistPrivacy(): ReadonlyMap<string, PlaylistPrivacy> {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
